import React, { useEffect, useState } from "react";
import { RefreshCw, Settings2, Sparkles, Undo2 } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import useAiModels from "../../../hooks/useAiModels";
import {
  AiModelPicker, AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, FormField, Textarea,
} from "../../ui";
import { aiRequestFromForm, applyDraft, formHasContent } from "./aiDraft";

const NEW_PLACEHOLDER =
  "e.g. Free 90-minute webinar next Saturday at 7 pm on landing your first data analyst job, for final-year "
  + "students and freshers. Speaker: <name>, <role> at <company>. Live portfolio review and Q&A at the end.";
const REFINE_PLACEHOLDER =
  "e.g. Make the FAQs shorter · add a 10-minute Q&A slot · move it to Sunday 6 pm · ask for graduation year when they register";

/**
 * EventAiPanel — "Draft this webinar with AI" at the top of the webinar form.
 *
 * The admin describes the event in a few lines; the draft fills the title,
 * description, event page, agenda, speakers and FAQs, plus any setting the
 * brief mentions (date, seats, reminders, certificate, registration questions,
 * WhatsApp group). It lands in the form, not the database: the admin reviews
 * it section by section and saves with the ordinary button.
 *
 * Once the form has content the same box refines it ("make the FAQs shorter").
 * Undo and "Try again" are offered until the admin edits anything by hand —
 * after that, restoring the old form would throw their edits away.
 */
const EventAiPanel = ({
  form, onApply, instructors = [], courses = [], apiBase, scheduleLocked = false, saveLabel, startOpen = true,
}) => {
  const [open, setOpen] = useState(startOpen);
  const ai = useAiModels(`${apiBase}/ai-draft/meta`, true);
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [showModel, setShowModel] = useState(false);

  useEffect(() => {
    if (!busy) return undefined;
    setElapsed(0);
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [busy]);

  // AI is switched off for this server — the form works exactly as before.
  if (ai.meta && ai.meta.enabled === false) return null;

  const quota = ai.meta?.quota;
  const blocked = quota && !quota.allowed;
  const maxChars = ai.meta?.maxBriefChars || 4000;
  const hasContent = formHasContent(form);
  const untouched = !!result && form === result.after;

  const run = async (text, base) => {
    if (text.trim().length < 8) {
      setError("Describe the event in a sentence or two.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await apiClient.post(`${apiBase}/ai-draft`, {
        prompt: text.trim(),
        ...aiRequestFromForm(base, { instructors, courses }),
        ...ai.payload,
      });
      const { form: next, changes } = applyDraft(base, data.draft, { instructors, courses, scheduleLocked });
      onApply(next);
      setResult({
        brief: text,
        changes,
        warnings: data.warnings || [],
        notes: data.draft?.notes || [],
        before: base,
        after: next,
      });
      setBrief("");
    } catch (err) {
      setError(err?.response?.data?.message || "The AI draft failed. Please try again.");
    }
    setBusy(false);
  };

  const undo = () => {
    onApply(result.before);
    setBrief(result.brief);
    setResult(null);
  };

  const checks = result ? [...result.warnings, ...result.notes] : [];

  return (
    <AppCard className="tw-border-primary/25">
      <AppCardHeader className="tw-flex-row tw-items-start tw-gap-3">
        <span
          className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary"
          aria-hidden="true"
        >
          <Sparkles size={18} />
        </span>
        <div className="tw-min-w-0 tw-flex-1">
          <AppCardTitle>{hasContent ? "Improve this webinar with AI" : "Draft this webinar with AI"}</AppCardTitle>
          <AppCardDescription>
            Describe it in a few lines. AI writes the title, description, event page, agenda and FAQs, and sets the
            date, reminders or certificate if you mention them. Nothing is saved until you review it
            and press “{saveLabel}”.
          </AppCardDescription>
        </div>
        {!open && (
          <AppButton type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>
            <Sparkles size={14} /> Use AI
          </AppButton>
        )}
      </AppCardHeader>

      {open && (
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          {blocked ? (
            <Callout tone="warning">{ai.meta.quotaMessage || "AI drafting isn't available on your plan."}</Callout>
          ) : (
            <>
              <FormField
                label={hasContent ? "What should AI change?" : "Describe your webinar"}
                hint={hasContent
                  ? "Or describe a different event to start over. Ctrl + Enter to send."
                  : "Topic, who it's for, date and time, speaker. Mention reminders, a certificate rule, registration questions or a WhatsApp group link to have those set too."}
              >
                <Textarea
                  rows={4}
                  value={brief}
                  maxLength={maxChars}
                  disabled={busy}
                  placeholder={hasContent ? REFINE_PLACEHOLDER : NEW_PLACEHOLDER}
                  onChange={(e) => setBrief(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      if (!busy) run(brief, form);
                    }
                  }}
                />
              </FormField>

              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <AppButton type="button" onClick={() => run(brief, form)} disabled={busy || !brief.trim()}>
                  <Sparkles size={16} /> {busy ? "Writing…" : hasContent ? "Update with AI" : "Draft with AI"}
                </AppButton>
                {busy ? (
                  <span role="status" className="tw-text-sm tw-text-content-muted">
                    Writing your webinar page · {elapsed}s — usually 10 to 30 seconds.
                  </span>
                ) : ai.pickable && (
                  <AppButton
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-expanded={showModel}
                    onClick={() => setShowModel((v) => !v)}
                  >
                    <Settings2 size={14} /> {ai.activeModel?.label || "AI model"}
                  </AppButton>
                )}
              </div>
              {showModel && !busy && <AiModelPicker {...ai} />}
            </>
          )}

          {error && <Callout tone="danger" role="alert">{error}</Callout>}

          {result && (
            <Callout
              tone="success"
              role="status"
              action={untouched && (
                <span className="tw-flex tw-flex-wrap tw-gap-2">
                  <AppButton type="button" size="sm" variant="outline" disabled={busy} onClick={() => run(result.brief, result.before)}>
                    <RefreshCw size={14} /> Try again
                  </AppButton>
                  <AppButton type="button" size="sm" variant="outline" disabled={busy} onClick={undo}>
                    <Undo2 size={14} /> Undo
                  </AppButton>
                </span>
              )}
            >
              {result.changes.length ? (
                <>
                  <p className="tw-m-0 tw-text-sm tw-font-semibold">
                    Filled in — review each section below, then press “{saveLabel}”.
                  </p>
                  <p className="tw-m-0 tw-mt-1 tw-text-sm">{result.changes.join(" · ")}</p>
                </>
              ) : (
                <p className="tw-m-0 tw-text-sm">AI had nothing to change. Try describing it differently.</p>
              )}
            </Callout>
          )}

          {checks.length > 0 && (
            <Callout tone="warning">
              <p className="tw-m-0 tw-text-sm tw-font-semibold">Check before publishing</p>
              <ul className="tw-m-0 tw-mt-1 tw-pl-4 tw-text-sm">
                {checks.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </Callout>
          )}
        </AppCardContent>
      )}
    </AppCard>
  );
};

export default EventAiPanel;
